import L from 'leaflet';

export interface USGSSimplePoint {
  id: number;
  name: string;
  monitoring_location_name?: string;
  system_type?: string;
  monitoring_location_id?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  site_type?: string;
  flood_stages?: {
    not_established?: boolean;
    stage?: {
      flow: number; //discharge value
      stage: number; //gage height value
    };
  };
  data?: any[];
}

/**
 * Convert USGS points to a GeoJSON FeatureCollection
 */
export const toGeoJSON = (points: USGSSimplePoint[]) => {
  return {
    type: 'FeatureCollection' as const,
    features: points.map((p) => ({
      type: 'Feature' as const,
      properties: p,
      geometry: {
        type: 'Point' as const,
        coordinates: [p?.coordinates?.lng, p?.coordinates?.lat],
      },
    })),
  };
};

// Maps normalized site type to the canvas shape to draw.
const TYPE_TO_SHAPE: Record<string, string> = {
  stream: 'circle',
  lake: 'square',
  wetland: 'triangle-left', // tip points left  (matches WetlandIcon SVG)
  estuary: 'triangle-right', // tip points right (matches EstuaryIcon SVG)
  coastal: 'triangle-down', // tip points down  (matches CoastalIcon SVG)
};

// Stroke colors matching USGSMarker BORDER_COLOR
const STROKE_COLORS: Record<string, string> = {
  major: 'rgb(221, 85, 204)',
  moderate: 'rgb(221, 68, 51)',
  minor: 'rgb(255, 153, 51)',
  action: 'rgb(255, 238, 0)',
  notFlooding: 'rgb(119, 187, 34)',
  noFloodStages: 'rgb(102, 102, 102)',
  measurement: 'rgb(153, 102, 51)',
  unavailable: 'rgb(0, 0, 0)',
};

const getFeatureStrokeColor = (feature: any): string => {
  const props = feature?.properties || {};
  const threshold = currentThreshold(usgsPointToDevice(props as USGSSimplePoint));
  return STROKE_COLORS[threshold] || STROKE_COLORS.unavailable;
};

/**
 * Custom canvas renderer that extends L.Canvas to support non-circle shapes.
 * All markers stay canvas-backed (no DOM elements per point) so performance
 * is identical to plain CircleMarker for large datasets.
 *
 * Reads a `shape` option from the CircleMarker options to decide which shape
 * to draw instead of the default circle.
 */
class ShapeCanvasRenderer extends L.Canvas {
  _updateCircle = (layer: any) => {
    const shape: string = layer.options?.shape || 'circle';
    if (shape === 'circle') {
      return (L.Canvas.prototype as any)._updateCircle.call(this, layer);
    }
    const self = this as any;
    if (!self._drawing || layer._empty()) return;
    const point = layer._point;
    const canvas: CanvasRenderingContext2D = self._ctx;
    const radius = Math.max(layer._radius, 1);
    canvas.beginPath();
    switch (shape) {
      case 'square':
        canvas.rect(point.x - radius, point.y - radius, radius * 2, radius * 2);
        break;
      case 'triangle-left': // wetland
        canvas.moveTo(point.x + radius, point.y - radius);
        canvas.lineTo(point.x - radius, point.y);
        canvas.lineTo(point.x + radius, point.y + radius);
        canvas.closePath();
        break;
      case 'triangle-right': // estuary
        canvas.moveTo(point.x - radius, point.y - radius);
        canvas.lineTo(point.x + radius, point.y);
        canvas.lineTo(point.x - radius, point.y + radius);
        canvas.closePath();
        break;
      case 'triangle-down': // coastal
        canvas.moveTo(point.x - radius, point.y - radius);
        canvas.lineTo(point.x + radius, point.y - radius);
        canvas.lineTo(point.x, point.y + radius);
        canvas.closePath();
        break;
      default:
        return (L.Canvas.prototype as any)._updateCircle.call(this, layer);
    }
    self._fillStroke(canvas, layer);
  };
}

/** Pixel radius scaled to the current zoom level. */
export const radiusForZoom = (zoom: number): number => {
  return Math.max(4, Math.min(14, Math.round(zoom * 0.8)));
};

/**
 * Returns a pointToLayer function that uses canvas-backed CircleMarker for all
 * points. Non-circle site types (lake, wetland, estuary, coastal) are drawn by
 * the custom ShapeCanvasRenderer instead of the default arc.
 *
 * @param getZoom - Optional callback returning the current map zoom, used to
 *   size the marker at creation time. Defaults to zoom 10 if omitted.
 */
export const createPointToLayer = (getZoom?: () => number) => {
  const shapeRenderer = new ShapeCanvasRenderer();
  return (feature: any, latlng: L.LatLng): L.CircleMarker => {
    const type = normalizeSiteType((feature?.properties as USGSSimplePoint)?.site_type);
    const shape = TYPE_TO_SHAPE[type] || 'circle';
    return L.circleMarker(latlng, {
      radius: radiusForZoom(getZoom?.() ?? 10),
      shape,
      fillColor: getFeatureFillColor(feature),
      color: getFeatureStrokeColor(feature),
      weight: 1,
      opacity: 1,
      fillOpacity: 1,
      renderer: shapeRenderer,
    } as any);
  };
};

/**
 * Basic helper to decide a per-feature fill color.
 * -
 *
 * @param {any} feature GeoJSON feature with `properties` set to the USGS point
 * @returns {string} CSS color (hex)
 */
export const getFeatureFillColor = (feature: any): string => {
  const props = feature?.properties || {};
  const fakeDevice = usgsPointToDevice(props as USGSSimplePoint);

  const threshold = currentThreshold(fakeDevice);

  // Match the color set used by the USGSMarker component
  const COLORS: Record<string, string> = {
    major: 'rgb(238, 170, 221)',
    moderate: 'rgb(238, 153, 136)',
    minor: 'rgb(255, 204, 136)',
    action: 'rgb(255, 255, 85)',
    notFlooding: 'rgb(170, 238, 102)',
    noFloodStages: 'rgb(255, 255, 255)',
    measurement: 'rgb(153, 153, 153)',
    unavailable: 'rgb(153, 153, 153)',
  };

  return COLORS[threshold] || COLORS.unavailable;
};

/**
 * Returns an "onEachFeature" function which attaches click and hover handlers.
 */
export const attachFeatureHandlers = (
  onClick: (props: USGSSimplePoint, latlng: L.LatLng) => void,
  onHover?: {
    onMouseOver?: (layer: L.Layer) => void;
    onMouseOut?: (layer: L.Layer) => void;
  },
) => {
  return (feature: any, layer: L.Layer) => {
    layer.on('click', (e: any) => {
      onClick(feature.properties as USGSSimplePoint, e.latlng as L.LatLng);
    });
    if (onHover?.onMouseOver) layer.on('mouseover', () => onHover.onMouseOver!(layer));
    if (onHover?.onMouseOut) layer.on('mouseout', () => onHover.onMouseOut!(layer));
  };
};

/**
 * Convert a USGS point into the connected-device popup device shape.
 */
export const usgsPointToDevice = (point: USGSSimplePoint) => {
  // Normalize incoming `point.data` into the legacy array-shaped datapoint the app expects.
  // Behavior:
  // - If `data` is already an array, return it unchanged.
  // - If `data` is an object keyed by timeSeriesId, convert to [{ ts, did, uid, values }].
  const generatedDataPoint = normalizeUSGSPointData((point as any).data);

  return {
    ...point,
    id: point.id,
    node_id: point.id,
    system_type: point.system_type ?? 'USGS',
    device_type: 'Monitoring Station',
    monitoring_location_name: point.monitoring_location_name ?? point.name,
    monitoring_location_id: point.monitoring_location_id,
    alias: point.monitoring_location_name ?? point.name,
    site_type: point.site_type ?? 'Stream',
    data: generatedDataPoint,
  };
};

// Normalize incoming USGS `data` into the legacy array-shaped datapoint the app expects.
// - If `data` is already an array, returns it unchanged.
// - If `data` is an object keyed by timeSeriesId, converts to [{ ts, did, uid, values }].
export const normalizeUSGSPointData = (rawData: any): any[] => {
  // If already an array, it's in the desired shape — return as-is.
  if (Array.isArray(rawData)) return rawData;
  // If missing or not an object (unexpected), return empty array (no datapoints).
  if (!rawData || typeof rawData !== 'object') return [];
  // Convert the keyed object into an array of [timeSeriesId, entry] pairs.
  const entryPairs = Object.entries(rawData);
  if (entryPairs.length === 0) return [];
  // Use the first entry to source metadata (timestamp, did, uid).
  const firstEntry = entryPairs[0][1] as any;
  // Prefer ISO `time` string (parse to ms); fallback to numeric `ts` if present.
  const timestamp = firstEntry.time ? Number(new Date(firstEntry.time)) : (firstEntry.ts ?? null);
  // Build the `values` map expected downstream: { [timeSeriesId]: { value, qualifier, approval_status } }
  const valuesMap = entryPairs.reduce(
    (acc: Record<string, any>, [timeSeriesId, entry]: [string, any]) => {
      acc[timeSeriesId] = {
        // Ensure missing numeric/empty values are represented as `null` for consistency.
        value: entry?.value ?? null,
        // Normalize qualifier into either `null` or an array of qualifiers.
        qualifier:
          entry?.qualifier == null
            ? null
            : Array.isArray(entry.qualifier)
              ? entry.qualifier
              : [entry.qualifier],
        approval_status: entry?.approval_status ?? null,
      };
      return acc;
    },
    {},
  );

  // Return a single datapoint array matching the legacy `{ ts, did, uid, values }` shape.
  return [
    {
      ts: timestamp,
      did: firstEntry.id ?? 0,
      uid: firstEntry.uid ?? 0,
      ...valuesMap,
    },
  ];
};

/* --------------------- Supercluster helpers --------------------- */
import Supercluster from 'supercluster';
import { currentThreshold, normalizeSiteType } from './usgsMarker.utils';

export type SuperclusterIndex = any;

export const buildSupercluster = (
  points: USGSSimplePoint[],
  opts?: { radius?: number; maxZoom?: number; minZoom?: number },
) => {
  const index = new Supercluster({
    radius: opts?.radius ?? 60,
    maxZoom: opts?.maxZoom ?? 16,
    minZoom: opts?.minZoom ?? 0,
  });
  index.load((toGeoJSON(points) as any).features);
  return index as SuperclusterIndex;
};

export const boundsToBBox = (bounds: L.LatLngBounds) => {
  return [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
};

export const clusterIconHtml = (count: number) => {
  return `
    <div style="background: #325277; border: 1px solid #000; border-radius: 50%; width: 40px; height: 40px; display:flex; align-items:center; justify-content:center;">
      <strong style="color: #FFF; font-size: 14px;">${count}</strong>
    </div>
  `;
};

export const clusterRadiusForCount = (count: number) => {
  // simple heuristic: base 12 + sqrt(count) * 2
  return Math.min(40, 12 + Math.sqrt(count) * 2);
};

export const clustersForMap = (index: SuperclusterIndex, bounds: L.LatLngBounds, zoom: number) => {
  const bbox = boundsToBBox(bounds);
  return index.getClusters(bbox, Math.round(zoom));
};
